import { useEffect, useMemo, useState, useTransition } from "react";
import { toast } from "sonner";
import { Icone } from "@/components/Icone";
import { preencher } from "@/lib/modelos";
import { enviarModeloAction } from "@/web/src/acoes";
import type { DetalheConversa } from "@/lib/telas/conversas";

type Modelo = DetalheConversa["modelos"][number];

/**
 * Modelo aprovado: o único jeito de falar com quem está fora da janela de 24h
 * na API oficial. Ver o texto final ANTES de confirmar é o ponto — modelo é
 * pago e vai para o WhatsApp de alguém.
 */
export function EnviarModelo({
  telefone,
  modelos,
  pessoa,
  custoUSD,
  variante = "cabecalho",
}: {
  telefone: string;
  modelos: Modelo[];
  pessoa: { nome: string | null; interesse: string | null };
  custoUSD: number;
  /** cabecalho: pílula com texto · icone: só o ícone, dentro da caixa de escrever · primario: o botão verde da janela fechada. */
  variante?: "cabecalho" | "icone" | "primario";
}) {
  const [aberto, setAberto] = useState(false);
  const [escolhido, setEscolhido] = useState(modelos[0]?.nome ?? "");
  const [manuais, setManuais] = useState<Record<number, string>>({});
  const [pendente, iniciar] = useTransition();

  const m = modelos.find((x) => x.nome === escolhido);
  const variaveis = useMemo(
    () =>
      m
        ? m.variaveis.map((v, i) => (v.fonte === "nome" ? (pessoa.nome ?? "") : v.fonte === "interesse" ? (pessoa.interesse ?? "") : (manuais[i] ?? "")))
        : [],
    [m, manuais, pessoa],
  );
  const previa = m ? preencher(m.previa, variaveis) : "";

  useEffect(() => {
    if (!aberto) return;
    const tecla = (e: KeyboardEvent) => e.key === "Escape" && setAberto(false);
    document.addEventListener("keydown", tecla);
    return () => document.removeEventListener("keydown", tecla);
  }, [aberto]);

  return (
    <>
      <button
        type="button"
        onClick={() => setAberto(true)}
        aria-label={variante === "icone" ? "Enviar modelo" : undefined}
        title={variante === "icone" ? "Enviar modelo" : undefined}
        className={
          variante === "icone"
            ? "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[var(--color-suave)] transition-colors hover:bg-[var(--color-painel)] hover:text-[var(--color-tinta)]"
            : variante === "primario"
              ? "flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-marca)] px-4 text-[13px] font-semibold whitespace-nowrap text-[var(--color-sobre-marca)] hover:brightness-110"
              : "flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-cartao)] px-4 text-[13px] font-semibold whitespace-nowrap text-[var(--color-tinta)] shadow-botao transition-colors hover:bg-[var(--color-painel)]"
        }
      >
        <Icone nome="modelos" className={variante === "icone" ? "h-5 w-5" : "h-4 w-4"} />
        {variante !== "icone" && "Enviar modelo"}
      </button>

      {aberto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onClick={() => setAberto(false)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Enviar modelo aprovado"
            className="anim-scale max-h-[86vh] w-full max-w-lg overflow-y-auto rounded-[18px] bg-[var(--color-elevado)] p-6 text-[var(--color-tinta)] shadow-flutua"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold">Enviar modelo aprovado</h2>
            <p className="mt-1 mb-4 text-[13px] leading-relaxed text-[var(--color-suave)]">
              {custoUSD > 0 ? `Cada envio custa US$ ${custoUSD.toFixed(4)}. ` : ""}
              Confira o texto final antes de confirmar — ele vai para o WhatsApp de alguém.
            </p>

            <div className="mb-4 space-y-1.5">
              {modelos.map((x) => (
                <label
                  key={x.nome}
                  className={
                    "flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 transition-colors " +
                    (escolhido === x.nome
                      ? "bg-[var(--color-realce)] shadow-[inset_0_0_0_1.5px_var(--color-marca)]"
                      : "shadow-[inset_0_0_0_1px_var(--color-borda)] hover:bg-[var(--color-painel)]")
                  }
                >
                  <input
                    type="radio"
                    className="accent-[var(--color-marca)]"
                    checked={escolhido === x.nome}
                    onChange={() => {
                      setEscolhido(x.nome);
                      setManuais({});
                    }}
                  />
                  <code className="flex-1 font-mono text-xs">{x.nome}</code>
                  <span className="rounded-full bg-[var(--color-painel)] px-2.5 py-0.5 text-xs font-semibold text-[var(--color-suave)] shadow-[inset_0_0_0_1px_var(--color-borda)]">{x.categoria}</span>
                </label>
              ))}
              {modelos.length === 0 && <p className="text-xs text-[var(--color-suave)]">Nenhum modelo cadastrado.</p>}
            </div>

            {m?.variaveis.map((v, i) =>
              v.fonte === "manual" ? (
                <div key={i} className="mb-3">
                  <label className="mb-1 block text-xs font-medium text-[var(--color-suave)]">
                    {`{{${i + 1}}}`} — {v.rotulo}
                  </label>
                  <input
                    value={manuais[i] ?? ""}
                    onChange={(e) => setManuais((x) => ({ ...x, [i]: e.target.value }))}
                    className="h-10 w-full rounded-[10px] border border-[var(--color-borda)] bg-[var(--color-painel)] px-3 text-sm text-[var(--color-tinta)] outline-none transition-colors focus:border-[var(--color-marca)] focus:bg-[var(--color-cartao)]"
                  />
                </div>
              ) : null,
            )}

            {m && (
              <div className="my-4">
                <div className="bg-chat flex flex-col items-end gap-2 rounded-[14px] p-3.5">
                  <p className="rotulo self-start">O que vai sair</p>
                  <p className="max-w-[90%] rounded-[14px] rounded-tr-[4px] bg-[var(--color-cartao)] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap text-[var(--color-tinta)] shadow-[inset_0_0_0_1.5px_var(--color-marca)]">
                    {previa}
                  </p>
                </div>
                {previa.includes("{{") && (
                  <p className="mt-1.5 text-[11px] text-[var(--color-alerta)]">
                    Sobrou marcador no texto — preencha antes de enviar.
                  </p>
                )}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setAberto(false)} className="h-9 rounded-full px-4 text-[13px] font-semibold text-[var(--color-suave)] transition-colors hover:bg-[var(--color-painel)] hover:text-[var(--color-tinta)]">
                Cancelar
              </button>
              <button
                type="button"
                disabled={!m || pendente || previa.includes("{{")}
                onClick={() =>
                  iniciar(async () => {
                    const r = await enviarModeloAction(telefone, escolhido, variaveis);
                    if (!r.ok) {
                      toast.error(r.erro);
                      return;
                    }
                    toast.success("Modelo enviado.");
                    setAberto(false);
                  })
                }
                className="h-9 rounded-full bg-[var(--color-marca)] px-4 text-[13px] font-semibold text-[var(--color-sobre-marca)] transition-[filter] hover:brightness-110 disabled:opacity-40"
              >
                {pendente ? "Enviando…" : "Confirmar envio"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
